/**
 * Abstract base class for all web service clients.
 */
export class WebServiceClient {
  private readonly baseUrl: string
  private params: { [ key: string ]: string }
  private path: string

  /**
   * Construct a base webservice.
   * @param baseUrl The base URL of the webservice (without any parameters).
   * @param params The parameters given to the webservice. These will be URI
   * encoded automatically so there is no need for encoding in this argument.
   */
  constructor (baseUrl: string, params: { [key: string ]: string } | undefined) {
    this.baseUrl = baseUrl
    this.path = ''
    if (params === undefined) {
      this.params = {}
    } else {
      this.params = params
    }
  }

  /**
   * Add a path to the base URL.
   * @param path The path to add.
   */
  public setPath (path: string): void {
    this.path = path
  }

  /**
   * Sets the given parameters.
   * @param params The parameters to be set. These will be URI
   * encoded automatically so there is no need for encoding in this argument.
   */
  public setParams (params: { [key: string ]: string }): void {
    this.params = params
  }

  /**
   * Add a single parameter to the list of parameters. If a parameter
   * with the given key already exists it will be overwritten.
   * @param key The key of the parameter to add.
   * @param value The value of the parameter to add.
   */
  public addParam (key: string, value: string): void {
    this.params[key] = value
  }

  /**
   * Check whether a given parameter is present.
   * @param key The key to search for.
   * @returns Whether or not the parameter list contains the given key.
   */
  public hasParam (key: string): boolean {
    return key in this.params
  }

  /**
   * Getter for the entire URL needed to invoke the web service
   * with the given parameters.
   */
  public url (): string {
    return `${this.baseUrl}${this.path}?${this.urlEncodedParams}`
  }

  /**
   * Getter for the parameter string.
   */
  private get urlEncodedParams (): string {
    const paramTokens = Object.keys(this.params)
      .reduce((acc: string[], val: string): string[] => {
        let token = `${encodeURIComponent(val)}`
        if (this.params[val] !== '') {
          token = `${token}=${encodeURIComponent(this.params[val])}`
        }
        return acc.concat([token])
      }, [])
    const paramString = paramTokens.join('&')
    return paramString
  }
}
